/** @typedef {'percentage'|'fixed_per_person'|'fixed_total'|'none'} OrgTourSupplierCommissionKind */

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function upper(s) {
  return String(s || "").trim().toUpperCase();
}

/**
 * Read normalized value from BE-organized_tour_ssot_trace rows (if present).
 * @param {unknown} ssotTrace
 * @param {string} field
 */
export function pickSsotTraceNormalized(ssotTrace, field) {
  if (!Array.isArray(ssotTrace)) return undefined;
  const row = ssotTrace.find((r) => r && r.field === field);
  if (!row) return undefined;
  const { normalized, raw } = row;
  return normalized != null ? normalized : raw;
}

/**
 * Map LDS / snapshot commission_type to a small UI union.
 * @param {string} raw
 * @returns {OrgTourSupplierCommissionKind}
 */
export function mapCommissionTypeToKind(raw) {
  const u = upper(raw);
  if (!u || u === "NET") return "none";
  if (u === "PERCENT" || u === "PERCENTAGE") return "percentage";
  if (u === "FIXED_PER_PERSON" || u === "FIXED PER PERSON") return "fixed_per_person";
  if (u === "AMOUNT" || u === "FIXED_TOTAL" || u === "FIXED") return "fixed_total";
  return "none";
}

function kindLabelHe(kind) {
  switch (kind) {
    case "percentage":
      return "אחוזים";
    case "fixed_per_person":
      return "פיקס לאדם";
    case "fixed_total":
      return "פיקס כולל לעסקה";
    default:
      return "ללא";
  }
}

function formatSrcAmount(amt, cur) {
  if (amt == null || !Number.isFinite(Number(amt))) return null;
  const c = String(cur || "").trim().toUpperCase() || "—";
  return `${formatTraceNumberStyled(Number(amt))} ${c}`.trim();
}

function formatTraceNumberStyled(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  if (Math.abs(v - Math.round(v)) < 1e-9) return String(Math.round(v));
  return String(Math.round(v * 100) / 100);
}

/**
 * Builds supplier commission display facts for Organized Tour Price Trace.
 * Does not invent configured percents; computes effective % only from gross & deduction when possible.
 *
 * @param {{
 *   snap: Record<string, unknown> | null,
 *   fareDetails: Record<string, unknown> | null,
 *   gross: number | null,
 *   net: number | null,
 * }} p
 */
export function resolveOrganizedTourSupplierCommissionDisplay({ snap, fareDetails, gross, net }) {
  const snapR = snap && typeof snap === "object" ? snap : null;
  const fd = fareDetails && typeof fareDetails === "object" ? fareDetails : null;
  const commFd = fd?.commission && typeof fd.commission === "object" ? fd.commission : null;
  const cur =
    String(fd?.currency || snapR?.currency || "").trim().toUpperCase() ||
    "—";

  const ssot = snapR?.organized_tour_ssot_trace;

  const grossN = num(gross);
  const netN = num(net);

  let deduction = num(snapR?.supplier_deduction);
  if (deduction == null && grossN != null && netN != null) {
    deduction = grossN - netN;
  }

  const rule =
    snapR?.supplier_rule && typeof snapR.supplier_rule === "object" ? snapR.supplier_rule : null;
  const ruleType = upper(rule?.commission_type);
  const snapType = upper(snapR?.supplier_commission_type);
  const fdType = upper(commFd?.commissionType);

  let kind = /** @type {OrgTourSupplierCommissionKind} */ ("none");
  let typeSource = "";

  const ssotKindRaw = pickSsotTraceNormalized(ssot, "supplier_commission_type");
  if (typeof ssotKindRaw === "string" && upper(ssotKindRaw)) {
    kind = mapCommissionTypeToKind(ssotKindRaw);
    typeSource = "organized_tour_ssot_trace";
  } else if (ruleType) {
    kind = mapCommissionTypeToKind(ruleType);
    typeSource = "pricingSnapshot.supplier_rule";
  } else if (snapType) {
    kind = mapCommissionTypeToKind(snapType);
    typeSource = "pricingSnapshot.supplier_commission_type";
  } else if (fdType === "AMOUNT") {
    kind = "fixed_total";
    typeSource = "fareDetails.commission";
  } else if (fdType === "PERCENT" || fdType === "PERCENTAGE") {
    kind = "percentage";
    typeSource = "fareDetails.commission";
  } else if (fdType === "FIXED_PER_PERSON") {
    kind = "fixed_per_person";
    typeSource = "fareDetails.commission";
  }

  /** Configured value display (not effective %). */
  let valueDisplay = null;
  if (kind === "percentage") {
    let p = null;
    if (rule && (num(rule.commission_percentage) != null || num(rule.discount_percentage) != null)) {
      p =
        (num(rule.commission_percentage) || 0) + (num(rule.discount_percentage) || 0);
      typeSource = typeSource || "pricingSnapshot.supplier_rule";
    } else if (num(commFd?.commissionValue) != null && fdType === "PERCENT") {
      p = num(commFd.commissionValue);
      typeSource = typeSource || "fareDetails.commission";
    }
    if (p != null && Number.isFinite(p)) valueDisplay = `${p}%`;
  } else if (kind === "fixed_per_person") {
    const pp = num(rule?.fixed_commission_per_person) ?? num(pickSsotTraceNormalized(ssot, "supplier_commission_value"));
    if (pp != null && Number.isFinite(pp)) {
      valueDisplay = `${formatTraceNumberStyled(pp)} ${cur} per pax`;
    }
  } else if (kind === "fixed_total") {
    const base = commFd?.commissionBase;
    const v = num(commFd?.commissionValue);
    if (v != null && upper(base) === "TOTAL_FARE") {
      valueDisplay = `${formatTraceNumberStyled(v)} ${cur} fixed`;
    } else if (v != null) {
      valueDisplay = `${formatTraceNumberStyled(v)} ${cur} (${String(base || "")})`;
    }
  }

  let effectivePercent = null;
  if (deduction != null && grossN != null && grossN > 0 && Number.isFinite(deduction)) {
    effectivePercent = (100 * deduction) / grossN;
  }

  let deductionStatus = "ok";
  /** @type {string | null} */
  let deductionStatusMessage = null;
  if (deduction == null) {
    deductionStatus = "not_available";
    deductionStatusMessage = "supplier_commission_not_available";
  } else if (Math.abs(deduction) < 1e-9) {
    deductionStatus = "zero";
    deductionStatusMessage = "no_supplier_commission_applied";
  }

  const amountFromSsot = pickSsotTraceNormalized(ssot, "supplier_commission_amount_source_currency");
  const dedForDisplay = num(amountFromSsot) != null ? num(amountFromSsot) : deduction;

  let deductionDisplay =
    dedForDisplay != null && Number.isFinite(dedForDisplay)
      ? formatSrcAmount(dedForDisplay, cur)
      : deductionStatus === "not_available"
        ? "supplier_commission_not_available"
        : "—";
  if (
    deductionStatus === "zero" &&
    dedForDisplay != null &&
    Number.isFinite(dedForDisplay) &&
    grossN != null
  ) {
    deductionDisplay = `${formatSrcAmount(0, cur)} · no_supplier_commission_applied`;
  }

  return {
    currency: cur,
    gross: grossN,
    net: netN,
    deduction: dedForDisplay != null ? dedForDisplay : deduction,
    deductionStatus,
    deductionStatusMessage,
    /** @type {OrgTourSupplierCommissionKind} */
    supplier_commission_type: kind,
    supplier_commission_type_he: kindLabelHe(kind),
    supplier_commission_value_display: valueDisplay,
    supplier_commission_amount_source_currency_display: deductionDisplay,
    supplier_commission_percent_effective: effectivePercent,
    supplier_commission_percent_effective_display:
      effectivePercent != null && grossN != null && grossN > 0
        ? `${effectivePercent.toFixed(2)}%`
        : null,
    type_source: typeSource,
  };
}
